import { useRef, useState, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ORDER_STATUSES } from "./status-badge";
import { cn } from "@/lib/utils";
import { Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { detectOrderSearchScope, overrideSurvives, SCOPE_LABELS, type OrderSearchScope } from "@/lib/order-search-scope";

type Props = {
  status: string;
  search: string;
  itemSearch: string;
  /** Webhook (mirror) stores get one input that ORs customer and item matches. */
  unifiedSearch?: boolean;
  q: string;
  qScope: OrderSearchScope | null;
  onQChange: (q: string) => void;
  onQScopeChange: (scope: OrderSearchScope | null) => void;
  paymentMethod: string;
  shippingMethod: string;
  dateAfter: string;
  dateBefore: string;
  shippedFilter: string;
  onStatusChange: (status: string) => void;
  onSearchChange: (search: string) => void;
  onItemSearchChange: (search: string) => void;
  onPaymentMethodChange: (method: string) => void;
  onShippingMethodChange: (method: string) => void;
  onDateAfterChange: (date: string) => void;
  onDateBeforeChange: (date: string) => void;
  onShippedFilterChange: (value: string) => void;
  shippingMethods?: { id: string; method_id: string; instance_id: number; title: string }[];
  paymentGateways?: { id: string; title: string }[];
};

function useDebouncedCallback(callback: (value: string) => void, delay: number) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  return useCallback((value: string) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => callbackRef.current(value), delay);
  }, [delay]);
}

export function OrderFilters(props: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const hasDateFilter = props.dateAfter || props.dateBefore;

  const [localSearch, setLocalSearch] = useState(props.search);
  const [localItemSearch, setLocalItemSearch] = useState(props.itemSearch);
  const [localQ, setLocalQ] = useState(props.q);

  const debouncedSearch = useDebouncedCallback(props.onSearchChange, 400);
  const debouncedItemSearch = useDebouncedCallback(props.onItemSearchChange, 400);
  const debouncedQ = useDebouncedCallback(props.onQChange, 400);

  useEffect(() => { setLocalSearch(props.search); }, [props.search]);
  useEffect(() => { setLocalItemSearch(props.itemSearch); }, [props.itemSearch]);
  useEffect(() => { setLocalQ(props.q); }, [props.q]);

  // Detected scope drives the query until the user overrides it with a chip.
  const activeScope: OrderSearchScope = props.qScope ?? detectOrderSearchScope(localQ);

  function handleQChange(value: string) {
    const prev = localQ;
    setLocalQ(value);
    debouncedQ(value);
    // Typing a different term (not refining the current one) drops the manual scope,
    // so a leftover "Artikli" can't swallow an order number pasted into the same field.
    if (props.qScope !== null && !overrideSurvives(prev, value)) props.onQScopeChange(null);
  }

  function clearQ() {
    setLocalQ("");
    props.onQChange("");
    props.onQScopeChange(null);
  }

  function clearDates() {
    props.onDateAfterChange("");
    props.onDateBeforeChange("");
  }

  return (
    <div className="flex flex-col gap-3">
      <div
        ref={scrollRef}
        className="flex gap-0 overflow-x-auto scrollbar-none border-b border-foreground/10"
      >
        {ORDER_STATUSES.map((s) => (
          <button
            key={s.value}
            type="button"
            onClick={() => props.onStatusChange(s.value)}
            className={cn(
              "shrink-0 px-3 pb-2 pt-0.5 text-xs font-medium transition-colors relative",
              "hover:text-foreground",
              props.status === s.value
                ? "text-foreground"
                : "text-muted-foreground"
            )}
          >
            {s.label}
            {props.status === s.value && (
              <span className="absolute bottom-0 left-3 right-3 h-px bg-foreground" />
            )}
          </button>
        ))}
      </div>

      {props.unifiedSearch ? (
        <div className="flex flex-col gap-1.5">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Pretraži narudžbe (br, kupac, email, tracking, artikal, SKU)..."
              value={localQ}
              onChange={(e) => handleQChange(e.target.value)}
              className="pl-8 pr-8"
            />
            {localQ && (
              <button
                type="button"
                onClick={clearQ}
                aria-label="Očisti pretragu"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {localQ.trim() && (
            <div className="flex items-center gap-1.5 text-[11px]">
              <span className="text-muted-foreground">Traži u:</span>
              {(["all", "customer", "items"] as OrderSearchScope[]).map((scope) => (
                <button
                  key={scope}
                  type="button"
                  onClick={() => props.onQScopeChange(scope === activeScope ? null : scope)}
                  className={cn(
                    "px-2 py-0.5 ring-1 transition-colors",
                    scope === activeScope
                      ? "bg-foreground text-background ring-foreground"
                      : "text-muted-foreground ring-foreground/15 hover:text-foreground"
                  )}
                >
                  {SCOPE_LABELS[scope]}
                </button>
              ))}
              {props.qScope === null && (
                <span className="text-muted-foreground/70">· automatski</span>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="flex gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Pretraži (br, kupac, email)..."
              value={localSearch}
              onChange={(e) => { setLocalSearch(e.target.value); debouncedSearch(e.target.value); }}
              className="pl-8"
            />
          </div>
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Pretraži artikle (naziv, SKU)..."
              value={localItemSearch}
              onChange={(e) => { setLocalItemSearch(e.target.value); debouncedItemSearch(e.target.value); }}
              className="pl-8"
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        <Select value={props.paymentMethod || "all"} onValueChange={props.onPaymentMethodChange}>
          <SelectTrigger className="w-full h-8 text-xs">
            <SelectValue placeholder="Plaćanje" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Sva plaćanja</SelectItem>
            {props.paymentGateways?.map((g) => (
              <SelectItem key={g.id} value={g.id}>{g.title}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={props.shippedFilter || "all"} onValueChange={props.onShippedFilterChange}>
          <SelectTrigger className="w-full h-8 text-xs">
            <SelectValue placeholder="Uvoz kuriru" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Sve narudžbe</SelectItem>
            <SelectItem value="shipped">Samo uvezene</SelectItem>
            <SelectItem value="not_shipped">Samo neuvezene</SelectItem>
          </SelectContent>
        </Select>
        <Select value={props.shippingMethod || "all"} onValueChange={props.onShippingMethodChange}>
          <SelectTrigger className="w-full h-8 text-xs">
            <SelectValue placeholder="Dostava" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Sve dostave</SelectItem>
            {props.shippingMethods?.map((m) => (
              <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <DatePicker
          value={props.dateAfter}
          onChange={props.onDateAfterChange}
          placeholder="Od datuma"
          className="w-full h-8 text-xs"
        />
        <div className="flex gap-1">
          <DatePicker
            value={props.dateBefore}
            onChange={props.onDateBeforeChange}
            placeholder="Do datuma"
            className="w-full h-8 text-xs"
          />
          {hasDateFilter && (
            <Button
              variant="ghost"
              size="icon"
              onClick={clearDates}
              className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
